/**
 * P3-08: useButtonModel —— Bonfire tab 按钮的"每 tick fetchModel 快照"订阅。
 *
 * 对齐旧 Button.update 语义（core.js:951-960）：每 tick 调
 * `controller.fetchModel(opts)` 重建 model，组件只在快照字段变化时重渲染
 * （旧版则每 tick 无条件重渲 DOM，此处以快照比对达成"仅必要重渲染"，见
 * dev-plan §3.9 P3-48 性能语义）。
 *
 * 约定（INVENTORY 风险点 6）：
 * - model 与 model 内 handler（x100Link/stageLinks 等）由 fetchModel 每次重建，
 *   闭包捕获当次 model；组件持有 modelRef（最近一次 fetch 产物）供点击回调
 *   使用，与旧版 this.model 语义一致（ref 仅在事件回调/selector 内读写，
 *   不在渲染期访问——react-hooks/refs 约束）；
 * - controller 由调用侧 useMemo 创建（game 就绪后稳定）；
 * - opts 每次选择器求值时重建（i18n 文案、twoRow、isEnriched 价格等输入
 *   随 tick 重取，对齐旧 render 期求值）。
 *
 * 前提：本 hook 的组件在 gamePage 就绪后挂载（MidPanel 于 index.html initGame
 * 末尾挂载）；gamePage 为 null 时快照恒 null、组件渲染 null。
 */
import { useCallback, useLayoutEffect, useRef } from "react";
import type { MutableRefObject } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { useGameTick } from "../../bridge/useGameTick";

/** 旧 ButtonModernController 族的最小面（core.js:606-843）
 * 多数按钮走 buyItem；JobButton 等少数按钮走 clickHandler。 */
export interface ModernButtonController {
  fetchModel(opts: Record<string, unknown>): Record<string, unknown>;
  buyItem?(model: unknown, event?: unknown): { itemBought: boolean; reason: string };
  clickHandler?(model: unknown, event?: unknown): void;
}

export interface ButtonModelBinding<TSnap> {
  /** 渲染用快照；游戏未就绪为 null */
  snap: TSnap | null;
  /** 最近一次 fetchModel 产物（点击/link 回调用，对齐旧 this.model） */
  modelRef: MutableRefObject<Record<string, unknown> | null>;
}

export function useButtonModel<TSnap>(
  controller: ModernButtonController | null,
  createOpts: (game: GamePage) => Record<string, unknown>,
  toSnapshot: (model: Record<string, unknown>) => TSnap,
  snapshotEq: (a: TSnap | null, b: TSnap | null) => boolean,
): ButtonModelBinding<TSnap> {
  const modelRef = useRef<Record<string, unknown> | null>(null);

  const selector = useCallback(
    (game: GamePage | null): TSnap | null => {
      if (!game || !controller) {
        return null;
      }
      return toSnapshot(controller.fetchModel(createOpts(game)));
    },
    [controller, createOpts, toSnapshot],
  );

  const snap = useGameTick(selector, snapshotEq);

  // P3-48: modelRef 在 commit 阶段更新，避免在 render/getSnapshot 中
  // 突变 ref 触发 React 18 useSyncExternalStore 的嵌套更新检测。
  useLayoutEffect(() => {
    const game = getGame();
    if (game && controller && snap) {
      modelRef.current = controller.fetchModel(createOpts(game));
    }
  }, [snap, controller, createOpts]);

  return { snap, modelRef };
}

/** 扁平快照的逐字段 Object.is 比对 */
export function shallowSnapEq<TSnap>(a: TSnap | null, b: TSnap | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  const ra = a as Record<string, unknown>;
  const rb = b as Record<string, unknown>;
  const keys = Object.keys(ra);
  if (keys.length !== Object.keys(rb).length) {
    return false;
  }
  for (const key of keys) {
    if (!Object.is(ra[key], rb[key])) {
      return false;
    }
  }
  return true;
}

/**
 * ButtonModernHelper.getTooltipHTML 的全局引用（core.js:1414-1497，classic
 * script 顶层 var → window 属性）。tooltip 回调每次调用内部会重新
 * fetchModel + fetchExtendedModel（core.js:1415-1418），与旧
 * game.tooltipUpdateFunc 机制等价。
 */
declare global {
  interface Window {
    /** 旧 dojo.declare 的 classes 全局（Bonfire tab 用到的 controller 构造器） */
    classes?: {
      game?: {
        ui?: {
          GatherCatnipButtonController?: new (game: GamePage) => ModernButtonController;
          RefineCatnipButtonController?: new (game: GamePage) => ModernButtonController;
        };
      };
      ui?: {
        btn?: {
          BuildingBtnModernController?: new (game: GamePage) => ModernButtonController;
          StagingBldBtnController?: new (game: GamePage) => ModernButtonController;
        };
      };
    };
    ButtonModernHelper?: {
      getTooltipHTML(
        controller: ModernButtonController,
        model: { options: Record<string, unknown> },
      ): string;
    };
  }
}

/**
 * 组装 tooltip 内容回调：直传 controller 与当次 opts（GameButton getTooltip 用）。
 * controller 经 getter 延迟求值，调用侧传 useMemo 产物的闭包（不触碰 ref）。
 */
export function makeModernTooltip(
  getController: () => ModernButtonController | null,
  createOpts: (game: GamePage) => Record<string, unknown>,
): () => string {
  return () => {
    const game = getGame();
    const controller = getController();
    const helper = window.ButtonModernHelper;
    if (!game || !controller || !helper) {
      return "";
    }
    return helper.getTooltipHTML(controller, { options: createOpts(game) });
  };
}
